import { Head, router, useForm, usePage } from "@inertiajs/react"

import {
  type Business,
  type Neighborhood,
  RaffleForm,
  type RaffleFormData,
} from "@/components/raffles/raffle-form"
import AppLayout from "@/layouts/app-layout"
import { rafflesPath } from "@/routes"

interface RafflesNewProps {
  raffle: Record<string, unknown>
  neighborhoods: Neighborhood[]
  businesses: Business[]
}

interface ViralCampaignForm {
  base_entries?: number | ""
  entries_per_referral?: number | ""
  max_entries_per_user?: number | ""
  status?: "draft" | "active" | "ended" | "drawn" | ""
}

export default function RafflesNew({
  neighborhoods,
  businesses,
}: RafflesNewProps) {
  const page = usePage()
  const search = page.url.includes("?") ? page.url.split("?")[1] : ""
  const params = new URLSearchParams(search)
  const from = params.get("from")

  const { data, setData, processing, errors } = useForm<{
    raffle: RaffleFormData & {
      create_viral_campaign: boolean
      viral_campaign: ViralCampaignForm
    }
  }>({
    raffle: {
      title: "",
      description: "",
      prize_description: "",
      prize_value: "",
      start_date: "",
      end_date: "",
      city_id: (neighborhoods?.[0]?.city?.id ?? "") as string | number,
      neighborhood_id: (neighborhoods?.[0]?.id ?? "").toString(),
      max_participants: "",
      winner_count: "1",
      sponsor_id: (businesses?.[0]?.id ?? "").toString(),
      terms_and_conditions: "",
      requirements: {},
      image: null,
      image_url: "",
      remove_image: false,
      create_viral_campaign: false,
      viral_campaign: {
        base_entries: 1,
        entries_per_referral: 5,
        max_entries_per_user: 100,
        status: "active",
      },
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const fd = new FormData()
    const r = data.raffle
    // Flat raffle fields
    fd.append("raffle[title]", r.title)
    fd.append("raffle[description]", r.description)
    fd.append("raffle[prize_description]", r.prize_description)
    fd.append("raffle[prize_value]", String(r.prize_value ?? ""))
    fd.append("raffle[start_date]", r.start_date)
    fd.append("raffle[end_date]", r.end_date)
    fd.append("raffle[city_id]", String(r.city_id ?? ""))
    fd.append("raffle[neighborhood_id]", String(r.neighborhood_id ?? ""))
    fd.append("raffle[max_participants]", String(r.max_participants ?? ""))
    fd.append("raffle[winner_count]", String(r.winner_count ?? ""))
    fd.append("raffle[sponsor_id]", String(r.sponsor_id ?? ""))
    fd.append("raffle[terms_and_conditions]", r.terms_and_conditions ?? "")
    fd.append("raffle[image_url]", r.image_url ?? "")
    fd.append("raffle[remove_image]", r.remove_image ? "1" : "0")
    // requirements (object)
    if (r.requirements) {
      Object.entries(r.requirements as Record<string, unknown>).forEach(
        ([k, v]) => {
          if (v != null) {
            const value =
              typeof v === "object"
                ? JSON.stringify(v)
                : String(v as string | number | boolean)
            fd.append(`raffle[requirements][${k}]`, value)
          }
        },
      )
    }
    // file image
    if (r.image instanceof File) {
      fd.append("raffle[image]", r.image)
    }
    // viral campaign options
    if (r.create_viral_campaign) {
      fd.append("raffle[create_viral_campaign]", "1")
      const vc = r.viral_campaign || {}
      if (vc.base_entries !== undefined && vc.base_entries !== "")
        fd.append(
          "raffle[viral_campaign][base_entries]",
          String(vc.base_entries),
        )
      if (
        vc.entries_per_referral !== undefined &&
        vc.entries_per_referral !== ""
      )
        fd.append(
          "raffle[viral_campaign][entries_per_referral]",
          String(vc.entries_per_referral),
        )
      if (
        vc.max_entries_per_user !== undefined &&
        vc.max_entries_per_user !== ""
      )
        fd.append(
          "raffle[viral_campaign][max_entries_per_user]",
          String(vc.max_entries_per_user),
        )
      if (vc.status) fd.append("raffle[viral_campaign][status]", vc.status)
    } else {
      fd.append("raffle[create_viral_campaign]", "0")
    }

    router.post(rafflesPath(), fd, { preserveScroll: true })
  }

  const handleCancel = () => {
    if (from) {
      router.visit(from)
    } else {
      router.visit(rafflesPath())
    }
  }

  const err = errors as unknown as Record<string, string>

  return (
    <AppLayout>
      <Head title="Crear sorteo" />

      <div className="mx-auto max-w-6xl p-4 md:p-6">
        <RaffleForm
          title="Crear sorteo"
          data={data.raffle}
          errors={err}
          neighborhoods={neighborhoods}
          businesses={businesses}
          processing={processing}
          onChange={(partial) =>
            setData("raffle", { ...data.raffle, ...partial })
          }
          onSubmit={submit}
          onCancel={handleCancel}
          submitLabel="Guardar"
          currentImage={null}
        />
      </div>
    </AppLayout>
  )
}
